/**
 * CR-6243 — branding image upload: field mapping and the did-it-actually-save
 * check, kept out of useAdminSettings so both can be tested directly.
 *
 * @link useAdminSettings.handleFileUpload for the caller
 * @link platformSettingsAPI.uploadBrandingImage for the multipart transport
 */

/** Tile key -> the model field the backend stores it on. */
export const BRANDING_FIELD_MAP: Record<string, string> = {
  companyLogo: 'company_logo',
  companyDarkLogo: 'company_dark_logo',
  headerLogo: 'header_logo',
  loginImage: 'login_image',
  termsConditions: 'terms_conditions',
  privacyPolicy: 'privacy_policy',
};

/** Tile key -> the accept filter on the file picker. */
export const BRANDING_ACCEPT_MAP: Record<string, string> = {
  companyLogo: 'image/png,image/jpeg,image/svg+xml',
  companyDarkLogo: 'image/png,image/jpeg,image/svg+xml',
  headerLogo: 'image/png,image/jpeg,image/svg+xml',
  loginImage: 'image/png,image/jpeg,image/webp',
  termsConditions: 'application/pdf',
  privacyPolicy: 'application/pdf',
};

/**
 * Tile key -> what the admin sees on screen. Toasts used to name the internal
 * key ("companyLogo uploaded successfully!"), which matches nothing on the page.
 */
export const BRANDING_FILE_LABELS: Record<string, string> = {
  companyLogo: 'Company light logo',
  companyDarkLogo: 'Company dark logo',
  headerLogo: 'Header logo',
  loginImage: 'Login background image',
  termsConditions: 'Terms & Conditions',
  privacyPolicy: 'Privacy Policy',
};

/**
 * The URL the server says it stored for `field`, or null if it stored nothing.
 *
 * A 2xx is not proof of a save: DRF drops keys its serializer does not know
 * without complaining, which is exactly how the header logo and login
 * background returned 200 while changing nothing. Callers must treat null as
 * a failure and must not report success.
 *
 * @param payload - the endpoint's response body (`{ message, data }`)
 * @param field - backend field name, e.g. 'company_logo'
 * @returns the saved URL, or null when the field is absent or empty
 */
export function savedImageUrl(payload: unknown, field: string): string | null {
  const data = (payload as { data?: unknown } | undefined)?.data;
  if (!data || typeof data !== 'object') return null;
  const value = (data as Record<string, unknown>)[field];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** Human label for a tile key, falling back to the key itself. */
export function brandingLabel(fileType: string): string {
  return BRANDING_FILE_LABELS[fileType] || fileType;
}
